import React from "react";
import { Select, Card, Typography } from "antd";
import { IoBarChart, IoCheckmarkCircle, IoWarningOutline } from "react-icons/io5";
import { MdDragIndicator } from "react-icons/md";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const listItemStyle = {
  cursor: "pointer",
  borderRadius: "4px",
  width: "360px",
  padding: "8px 16px",
  border: "1px solid #ddd",
  marginBottom: "8px",
  backgroundColor: "white",
  display: "flex",
  alignItems: "center",
};

const SortableChartItem = ({ chartState, stateKey, index, chartManager }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: `chart-${stateKey}-${index}`,
  });

  const isComplete =
    chartState.type !== "placeholder" &&
    Object.entries(chartState).every(([key, value]) => value !== "" || key.startsWith('subtitle'));

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      style={{
        ...listItemStyle,
        transform: CSS.Transform.toString(transform),
        transition,
        backgroundColor: isDragging ? "#e6f7ff" : "white",
      }}
      onClick={() => chartManager.handleEditChart(stateKey, index)}
      onMouseEnter={(e) => {
        if (!isDragging) e.currentTarget.style.backgroundColor = "#f5f5f5";
      }}
      onMouseLeave={(e) => {
        if (!isDragging) e.currentTarget.style.backgroundColor = "white";
      }}
    >
      <span
        {...listeners}
        onClick={(e) => e.stopPropagation()}
        style={{ display: "flex", alignItems: "center", marginRight: 8, cursor: "grab", color: "#999" }}
        aria-label="Drag to reorder"
      >
        <MdDragIndicator size={20} />
      </span>
      <IoBarChart size={20} style={{ marginRight: 8 }} />
      <div style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {chartState.title}
      </div>
      <div style={{ display: "flex", alignItems: "center", marginLeft: 8 }}>
        {isComplete ? (
          <IoCheckmarkCircle size={20} color="green" />
        ) : (
          <>
            <Typography.Text type="secondary" style={{ marginRight: 4, fontSize: "12px" }}>
              (Incomplete)
            </Typography.Text>
            <IoWarningOutline size={20} color="rgb(253,181,21)" />
          </>
        )}
      </div>
    </div>
  );
};

export const SingleCustomization = ({ chartManager, stateKey }) => {
  const stateCharts = chartManager.getAllStateCharts(stateKey);
  const positions = chartManager.getPositions(stateKey);
  const unusedPositions = [...positions, ...chartManager.unusedPositions];
  const usedPostions = chartManager.usedPositions.filter((pos) => !positions.includes(pos));

  const displayLabel =
    stateKey === "overview"
      ? "Overview Chart Customization"
      : "Position Chart Customization (" + chartManager.countIncompleteCharts(stateKey) + "/" +  "6)";

  const cardStyle = {
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const prefix = `chart-${stateKey}-`;
    const fromIndex = parseInt(active.id.slice(prefix.length));
    const toIndex = parseInt(over.id.slice(prefix.length));
    chartManager.handleReorderCharts(stateKey, fromIndex, toIndex);
  };

  return (
    <div style={{marginBottom:'16px'}}>
    <Card className="settings-card" style={cardStyle}>
      <Typography.Title level={5} style={{ marginBottom: "16px" }}>
        {displayLabel}
      </Typography.Title>

      {stateKey !== "overview" && (
        <>
          <Typography.Text style={{ marginBottom: "8px", display: "block" }}>
            Select the positions you wish to customize.
          </Typography.Text>
          <Select
            mode="multiple"
            style={{ width: "100%", marginTop: "8px",marginBottom:'8px' }}
            placeholder="Select unused positions"
            value={positions}
            onChange={(newPositions) =>
              chartManager.handleUpdateKeyWithPositions(newPositions, stateKey)
            }
          >
            {unusedPositions.map((pos) => (
              <Select.Option key={pos} value={pos}>
                {pos}
              </Select.Option>
            ))}
            {usedPostions.map((pos) => (
              <Select.Option key={pos} value={pos} disabled>
                {pos}
              </Select.Option>
            ))}
          </Select>
        </>
      )}

      <Typography.Text style={{ marginBottom: "16px", display: "block" }}>
        Drag to reorder. Click a chart to customize it.
      </Typography.Text>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={stateCharts.map((_, index) => `chart-${stateKey}-${index}`)}
          strategy={verticalListSortingStrategy}
        >
          <div style={{ marginTop: "16px" }}>
            {stateCharts.map((_, index) => {
              const chartState = chartManager.getStateCharts(stateKey, index);
              return (
                <SortableChartItem
                  key={`chart-${stateKey}-${index}`}
                  chartState={chartState}
                  stateKey={stateKey}
                  index={index}
                  chartManager={chartManager}
                />
              );
            })}
          </div>
        </SortableContext>
      </DndContext>
    </Card></div>
  );
};
